import React, { useMemo, useState } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { Badge, Button, Drawer, Layout, List, Space, message } from "antd";
import {
  BellOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import Logo from "./Logo";
import { onMobileNavToggle, toggleCollapsedNav } from "../../redux/actions/Theme";
import { NAV_TYPE_TOP, SIDE_NAV_COLLAPSED_WIDTH, SIDE_NAV_WIDTH } from "constants/ThemeConstant";
import utils from "utils";
import { useFirebase, useFirestore } from "react-redux-firebase";
import { useTranslation } from "react-i18next";
import * as dayjs from "dayjs";
import { DATE_FORMAT_DD_MM_YYYY_HH_mm } from "../../constants/DateConstant";
import { useCrmTasks } from "../../data/hooks/useCrmTasks";
import CustomerDrawer from "../shared-components/Customers/CustomerDrawer";
import OrderDrawer from "../shared-components/Orders/OrderDrawer";
import { FIRESTORE_USERS_TABLE } from "../../constants/FirestoreConstant";
import CrmContactMomentModal from "../shared-components/Customers/CrmContactMomentModal";

const { Header } = Layout;

export const HeaderNav = (props) => {
  const {
    navCollapsed,
    mobileNav,
    navType,
    headerNavColor,
    toggleCollapsedNav,
    // onMobileNavToggle,
    isMobile,
    currentTheme,
  } = props;
  const firebase = useFirebase();
  const firestore = useFirestore();
  const { t } = useTranslation();
  const authEmail = useSelector((state) => state.firebaseReducer.profile.email);
  const auth = useSelector((state) => state.firebaseReducer.auth);
  const allUsers = useSelector((state) => state.firestoreReducer.ordered.webshopUsers);
  const dispatch = useDispatch();
  const [messageApi, contextHolder] = message.useMessage();

  const [tasksDrawerOpen, setTasksDrawerOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDrawerVisible, setCustomerDrawerVisible] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);

  const [contactMomentModalVisible, setContactMomentModalVisible] = useState(false);
  const [contactMomentUserId, setContactMomentUserId] = useState(null);
  const [contactMomentCustomerName, setContactMomentCustomerName] = useState("");
  const [contactMomentText, setContactMomentText] = useState("");
  const [contactMomentDateTime, setContactMomentDateTime] = useState(null);
  const [contactMomentFileList, setContactMomentFileList] = useState([]);
  const [savingContactMoment, setSavingContactMoment] = useState(false);

  // Use the custom hook to fetch CRM tasks assigned to the current user
  const { tasks: assignedTasks } = useCrmTasks({
    assignedToUid: auth?.uid || null,
    autoFetch: !!auth?.uid,
    realtime: true,
  });

  const dueSoonTasks = useMemo(() => {
    const now = dayjs().unix();
    const soon = dayjs().add(24, "hour").unix();
    return (assignedTasks || [])
      .filter((t) => (t?.status || "open") === "open")
      .filter((t) => !t?.dueAt || (typeof t.dueAt === "number" && t.dueAt <= soon))
      .sort((a, b) => (a?.dueAt || 0) - (b?.dueAt || 0))
      .map((t) => ({
        ...t,
        _isOverdue: typeof t?.dueAt === "number" ? t.dueAt < now : false,
      }));
  }, [assignedTasks]);

  const badgeCount = dueSoonTasks.length;

  const onToggle = () => {
    if (!isMobile) {
      toggleCollapsedNav(!navCollapsed);
    } else {
      dispatch(onMobileNavToggle(!mobileNav));
    }
  };

  const isNavTop = navType === NAV_TYPE_TOP ? true : false;
  const mode = () => {
    if (!headerNavColor) {
      return utils.getColorContrast(currentTheme === "dark" ? "#00000" : "#ffffff");
    }
    return utils.getColorContrast(headerNavColor);
  };
  const navMode = mode();
  const getNavWidth = () => {
    if (isNavTop || isMobile) {
      return "0px";
    }
    if (navCollapsed) {
      return `${SIDE_NAV_COLLAPSED_WIDTH}px`;
    } else {
      return `${SIDE_NAV_WIDTH}px`;
    }
  };

  const handleSignOut = (e) => {
    e.preventDefault();
    firebase.logout().catch((err) => console.log(err));
  };

  const resetContactMomentState = () => {
    setContactMomentModalVisible(false);
    setContactMomentUserId(null);
    setContactMomentCustomerName("");
    setContactMomentText("");
    setContactMomentDateTime(null);
    setContactMomentFileList([]);
    setSavingContactMoment(false);
  };

  const openContactMomentModalForTask = (task) => {
    if (!task?.customerUserId) return;
    const customerName =
      task?.customerCompanyName || allUsers?.find((u) => u.id === task.customerUserId)?.company?.name || "";
    setContactMomentUserId(task.customerUserId);
    setContactMomentCustomerName(customerName);
    setContactMomentDateTime(new Date());
    setContactMomentText((prev) => {
      const trimmed = (prev || "").trim();
      if (trimmed) return prev;
      const taskTitle = (task?.title || "").trim();
      return taskTitle ? taskTitle : "";
    });
    setContactMomentFileList([]);
    setContactMomentModalVisible(true);
  };

  const saveContactMoment = async () => {
    if (!contactMomentUserId) return;
    if (!contactMomentText.trim() && contactMomentFileList.length === 0) {
      messageApi.warning(t("please_enter_note_or_attachment"));
      return;
    }

    try {
      setSavingContactMoment(true);
      const noteRef = firestore.collection(FIRESTORE_USERS_TABLE).doc(contactMomentUserId).collection("crmNotes").doc();

      const attachments = [];
      for (const file of contactMomentFileList) {
        if (file.originFileObj) {
          const storageRef = firebase.storage().ref(`users/${contactMomentUserId}/crmNotes/${noteRef.id}/${file.name}`);
          await storageRef.put(file.originFileObj);
          const url = await storageRef.getDownloadURL();
          attachments.push({ name: file.name, url, path: storageRef.fullPath });
        }
      }

      const now = dayjs().unix();
      const dateTimeUnix = contactMomentDateTime ? dayjs(contactMomentDateTime).unix() : now;

      await noteRef.set({
        text: contactMomentText.trim(),
        dateTime: dateTimeUnix,
        createdAt: now,
        createdBy: authEmail,
        createdByUid: auth.uid,
        attachments,
      });

      messageApi.success(t("saved"));
      resetContactMomentState();
    } catch (e) {
      console.error(e);
      messageApi.error(e?.message || String(e));
    } finally {
      setSavingContactMoment(false);
    }
  };

  const markTaskDone = async (task) => {
    if (!task?.customerUserId || !task?.id) return;
    try {
      await firestore
        .collection(FIRESTORE_USERS_TABLE)
        .doc(task.customerUserId)
        .collection("crmTasks")
        .doc(task.id)
        .update({
          status: "done",
          completedAt: Math.floor(Date.now() / 1000),
          completedBy: auth.email,
        });
      messageApi.success(t("task_completed"));

      openContactMomentModalForTask(task);
    } catch (e) {
      console.error(e);
      messageApi.error(e?.message || String(e));
    }
  };

  const openCustomerDrawer = (task) => {
    if (!task?.customerUserId) return;
    const customer = allUsers?.find((u) => u.id === task.customerUserId);
    if (customer) {
      setSelectedCustomer(customer);
      setCustomerDrawerVisible(true);
      setTasksDrawerOpen(false);
    }
  };

  return (
    <>
      {contextHolder}
      <Header className={`app-header ${navMode}`} style={{ backgroundColor: "#FFF" }}>
        <div className={`app-header-wrapper ${isNavTop ? "layout-top-nav" : ""}`}>
          <Logo logoType={navMode} />
          <div className="nav" style={{ width: `calc(100% - ${getNavWidth()})` }}>
            <div className="nav-left">
              {isNavTop && !isMobile ? null : navCollapsed || isMobile ? (
                <MenuUnfoldOutlined
                  className="nav-icon"
                  onClick={() => {
                    onToggle();
                  }}
                />
              ) : (
                <MenuFoldOutlined
                  className="nav-icon"
                  onClick={() => {
                    onToggle();
                  }}
                />
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Button
                type="text"
                onClick={() => setTasksDrawerOpen(true)}
                icon={
                  <Badge count={badgeCount} size="small" overflowCount={99}>
                    <BellOutlined style={{ fontSize: 18 }} />
                  </Badge>
                }
              />
            </div>
          </div>
        </div>
        <Drawer
          title={t("tasks")}
          placement="right"
          open={tasksDrawerOpen}
          onClose={() => setTasksDrawerOpen(false)}
          width={420}
        >
          <List
            dataSource={dueSoonTasks}
            locale={{ emptyText: t("no_data") }}
            renderItem={(task) => {
              const dueAt = typeof task?.dueAt === "number" ? dayjs.unix(task.dueAt) : null;
              return (
                <List.Item
                  key={task.id}
                  actions={[
                    <Button
                      key="done"
                      type="text"
                      icon={<CheckOutlined />}
                      onClick={() => markTaskDone(task)}
                      title={t("finish")}
                    />,
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                        <a onClick={() => openCustomerDrawer(task)} style={{ fontWeight: 600, cursor: "pointer" }}>
                          {task.title || "-"}
                        </a>
                        <span style={{ color: task._isOverdue ? "#ff4d4f" : "#999", whiteSpace: "nowrap" }}>
                          {dueAt ? dueAt.format(DATE_FORMAT_DD_MM_YYYY_HH_mm) : "-"}
                        </span>
                      </div>
                    }
                    description={
                      <div style={{ color: "#666" }}>
                        {task.customerCompanyName ? task.customerCompanyName : task.customerUserId || ""}
                      </div>
                    }
                  />
                </List.Item>
              );
            }}
          />
        </Drawer>

        <CrmContactMomentModal
          open={contactMomentModalVisible}
          onCancel={resetContactMomentState}
          onOk={saveContactMoment}
          confirmLoading={savingContactMoment}
          customerName={contactMomentCustomerName}
          dateTime={contactMomentDateTime}
          onDateTimeChange={setContactMomentDateTime}
          text={contactMomentText}
          onTextChange={setContactMomentText}
          fileList={contactMomentFileList}
          onFileListChange={setContactMomentFileList}
        />

        <CustomerDrawer
          user={selectedCustomer}
          visible={customerDrawerVisible}
          onClose={() => {
            setCustomerDrawerVisible(false);
            setSelectedCustomer(null);
          }}
          onOrderClick={(order) => setCurrentOrder(order)}
        />
        <OrderDrawer order={currentOrder} open={!!currentOrder} closeDrawer={() => setCurrentOrder(null)} />
      </Header>
    </>
  );
};

const mapStateToProps = ({ theme }) => {
  const { navCollapsed, navType, headerNavColor, mobileNav, currentTheme } = theme;
  return {
    navCollapsed,
    navType,
    headerNavColor,
    mobileNav,
    currentTheme,
  };
};

export default connect(mapStateToProps, { toggleCollapsedNav, onMobileNavToggle })(HeaderNav);
